import { docsArticlePath } from "@/lib/docs/parse";
import { networkLabel } from "@/lib/network";
import { ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

export type DemoBannerVariant = "full" | "mobile-soft";

/**
 * Play / local sandbox notice.
 * - full: solid strip (desktop under Agent Layer)
 * - mobile-soft: one thin line (under bottom tabs)
 * Production: null
 */
export function DemoNetworkBanner({
	compact = false,
	variant = "full",
}: {
	compact?: boolean;
	variant?: DemoBannerVariant;
}) {
	const net = networkLabel();
	if (net !== "play" && net !== "local") return null;

	const isPlay = net === "play";
	const envDocs = docsArticlePath("introduction", "environments");

	if (variant === "mobile-soft") {
		return (
			<div
				className={cn(
					"border-t border-border/80 bg-muted/95 px-3 py-1 text-center text-[10px] leading-snug text-muted-foreground backdrop-blur-sm",
					isPlay && "border-primary/25 bg-primary/10 text-foreground",
				)}
				data-play-banner="mobile-soft"
			>
				<span className="font-semibold">
					{isPlay ? "Play Mode sandbox" : "Local replica"}
				</span>
				<span className="opacity-80">
					{" — "}
					{isPlay ? "DEMO tokens" : "dev only"}
					{isPlay ? "; stack production-ready" : ""}
				</span>
				{" · "}
				<Link
					to={envDocs}
					className="font-semibold text-primary underline underline-offset-2"
				>
					Environments
				</Link>
			</div>
		);
	}

	const pad = compact ? "px-3 py-1.5 text-[11px]" : "px-3 py-2 text-xs";

	return (
		<div
			className={
				isPlay
					? `border-t border-primary-foreground/15 bg-primary text-primary-foreground ${pad}`
					: `border-t border-border bg-secondary text-secondary-foreground ${pad}`
			}
			data-play-banner="full"
		>
			<div className="container mx-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
				<p className="min-w-0 leading-snug">
					<strong className="font-semibold">
						{isPlay ? "Play Mode sandbox" : "Local replica"}
					</strong>
					{" — "}
					{isPlay
						? "DEMO collateral only. Real II + XRC prices; not real-money USDM/TVL."
						: "Local dev stack — not a mainnet money deployment."}{" "}
					<span className="opacity-90">
						CR: higher = safer (~1.50× mint / ~1.30× liq on{" "}
						<Link
							to={ROUTES.borrow}
							className="font-semibold underline underline-offset-2"
						>
							Borrow
						</Link>
						).{" "}
						<Link
							to={envDocs}
							className="font-semibold underline underline-offset-2"
						>
							Environments
						</Link>
					</span>
				</p>
				<span
					className={
						isPlay
							? "shrink-0 rounded-full border border-primary-foreground/35 bg-primary-foreground/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide"
							: "shrink-0 rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide"
					}
				>
					{isPlay ? "env · play · DEMO" : "env · local"}
				</span>
			</div>
		</div>
	);
}
